import { useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import {
  parseCsvTable,
  autoMapColumns,
  IMPORT_FIELDS,
  IMPORT_ENTITIES,
  type ImportEntity,
} from '@servwell/shared';
import { DataTable } from '@/components/ui/data-table';
import { useTRPC } from '../../lib/trpc';
import { PageRoot, PageHeader } from '../ui/Page';
import { SectionCard } from '../ui/Detail';
import { BackCrumb } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { IconCustomers, IconCheck } from '../icons';

/**
 * Bringing a shop's data over from their old tool (PRD v2 §7 V4 — Import-wizard re-skin).
 *
 * This is the FIRST thing a switching shop does, before they trust anything, and it is
 * where a migration is abandoned. So it is a stepper with a visible position, not a pile
 * of cards: an owner who can't tell how far through they are assumes it's harder than it is.
 *
 * Three things the v1 version didn't do, each of them a reason people gave up:
 *  · an example file to download, so "what should my CSV look like" has an answer
 *  · auto-matched columns marked as GUESSES, so they get reviewed rather than trusted
 *  · a preview of the actual records that will be created — not just a count of them
 */

const ENTITY_LABEL: Record<ImportEntity, string> = {
  customers: 'Customers',
  price_book: 'Price book',
};
const ENTITY_BLURB: Record<ImportEntity, string> = {
  customers: 'Names, addresses and contacts. Addresses become properties.',
  price_book:
    'Sell prices, SKUs, and optional one-time opening stock with unit cost. Re-imports keep initialized stock.',
};

/**
 * An example file, built from the same field list the importer validates against — so it
 * can never describe a format the server won't accept.
 */
function downloadExample(entity: ImportEntity): void {
  const fields = IMPORT_FIELDS[entity];
  const header = fields.map((f) => f.label).join(',');
  const sample =
    entity === 'customers'
      ? [
          'Alvarez Household,residential,,ana@example.com,614-555-0142,Columbus,OH,"812 Kenwood Ave",Home,Ana,Alvarez,ana@example.com,614-555-0142',
          'Northgate Property Mgmt,commercial,Northgate LLC,ap@northgate.example,614-555-0177,Columbus,OH,"40 Enterprise Pkwy",Building A,Dana,Reyes,dana@northgate.example,614-555-0178',
        ]
      : [
          'Furnace tune-up,service,149.00,,,,,"Annual clean and check",each,yes,HVAC',
          'Capacitor 45/5 MFD,material,38.50,CAP-4505,12.50,4,Main Warehouse,,each,yes,Parts',
        ];
  const blob = new Blob([[header, ...sample].join('\n')], {
    type: 'text/csv;charset=utf-8',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `servwell-${entity}-example.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const STEPS = ['What', 'Your file', 'Match columns', 'Check it'] as const;

export function ImportPage() {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [entity, setEntity] = useState<ImportEntity>('customers');
  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [guessed, setGuessed] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  // One key per file: a double-click imports once, but a SECOND file must import too.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  const headers = useMemo(() => (csv ? parseCsvTable(csv).headers : []), [csv]);

  const preview = useMutation(
    trpc.import.preview.mutationOptions({ onError: (e) => setError(e.message) }),
  );
  const commit = useMutation(
    trpc.import.commit.mutationOptions({
      onError: (e) => setError(e.message),
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  const resetFile = () => {
    setCsv('');
    setFileName('');
    setMapping({});
    setGuessed(new Set());
    setIdemKey(crypto.randomUUID());
    preview.reset();
    commit.reset();
    setError(null);
  };

  async function onFile(file: File) {
    const text = await file.text();
    const parsed = parseCsvTable(text);
    const auto = autoMapColumns(entity, parsed.headers);
    setCsv(text);
    setFileName(file.name);
    setMapping(auto);
    setGuessed(new Set(Object.keys(auto)));
    setIdemKey(crypto.randomUUID());
    preview.reset();
    commit.reset();
    setError(null);
  }

  const previewData = preview.data;
  const commitData = commit.data;
  const fields = IMPORT_FIELDS[entity];
  const unmappedRequired = fields.filter((f) => f.required && !mapping[f.key]);

  const step = commitData ? 4 : previewData ? 3 : headers.length > 0 ? 2 : csv ? 1 : 1;

  return (
    <PageRoot>
      <BackCrumb to="/settings" label="Settings" />
      <PageHeader
        title="Bring your data over"
        sub="Export a CSV from your old tool, match the columns, and see exactly what you'll get before anything is saved."
      />

      {/* Where you are. An owner who can't tell assumes it's harder than it is. */}
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        {STEPS.map((label, i) => {
          const n = i + 1;
          const done = step > n || Boolean(commitData);
          const here = step === n && !commitData;
          return (
            <li key={label} className="flex items-center gap-2">
              <span
                className={`flex size-6 items-center justify-center rounded-full text-xs font-bold ${
                  done
                    ? 'bg-[var(--sw-success-bg)] text-[var(--sw-success-text)]'
                    : here
                      ? 'bg-brand text-white'
                      : 'bg-surface-2 text-muted-foreground'
                }`}
              >
                {done ? <IconCheck size={13} /> : n}
              </span>
              <span className={here ? 'font-semibold' : 'text-muted-foreground'}>
                {label}
              </span>
              {n < STEPS.length ? <span className="text-muted-foreground">›</span> : null}
            </li>
          );
        })}
      </ol>

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {/* ── 1. What ──────────────────────────────────────────────────────── */}
      <SectionCard title="1 · What are you bringing over">
        <div className="grid gap-2 sm:grid-cols-2">
          {IMPORT_ENTITIES.map((en) => (
            <button
              key={en}
              type="button"
              onClick={() => {
                if (en === entity) return;
                setEntity(en);
                resetFile();
              }}
              aria-pressed={entity === en}
              className={`rounded-[var(--radius)] border p-3 text-left ${
                entity === en
                  ? 'border-brand bg-surface-2'
                  : 'border-border hover:bg-surface-2'
              }`}
            >
              <p className="font-semibold">{ENTITY_LABEL[en]}</p>
              <p className="text-xs text-muted-foreground">{ENTITY_BLURB[en]}</p>
            </button>
          ))}
        </div>
      </SectionCard>

      {/* ── 2. The file ──────────────────────────────────────────────────── */}
      <SectionCard
        title="2 · Your file"
        right={
          <Button variant="ghost" onClick={() => downloadExample(entity)}>
            Download an example
          </Button>
        }
      >
        <p className="mb-2 text-sm text-muted-foreground">
          A CSV with a header row. Column names don't have to match ours — you'll line
          them up next. Not sure what it should look like? Download the example above and
          fill it in.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-[var(--radius)] border border-border bg-surface px-4 text-sm font-semibold hover:bg-surface-2">
            {fileName || 'Choose a CSV file'}
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
              }}
            />
          </label>
          {fileName ? (
            <>
              <StatusBadge tone="success">
                {parseCsvTable(csv).rows.length} rows found
              </StatusBadge>
              <Button variant="ghost" onClick={resetFile}>
                Start over
              </Button>
            </>
          ) : null}
        </div>
      </SectionCard>

      {/* ── 3. Match ─────────────────────────────────────────────────────── */}
      {headers.length > 0 && !commitData ? (
        <SectionCard title="3 · Match your columns to ours">
          {guessed.size > 0 ? (
            <Notice tone="info">
              We've guessed {guessed.size} of these from your column names. Guesses are
              marked — have a look before you carry on, because a wrong one imports
              quietly.
            </Notice>
          ) : null}

          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {fields.map((spec) => {
              const isGuess = guessed.has(spec.key) && mapping[spec.key];
              return (
                <label key={spec.key} className="flex flex-col gap-1 text-sm">
                  <span className="flex items-center gap-1.5">
                    {spec.label}
                    {spec.required ? (
                      <span className="text-[var(--sw-danger-text)]">*</span>
                    ) : null}
                    {isGuess ? (
                      <span className="rounded-full bg-surface-2 px-1.5 text-[10px] font-semibold uppercase text-muted-foreground">
                        guessed
                      </span>
                    ) : null}
                  </span>
                  {spec.hint ? (
                    <span className="text-xs text-muted-foreground">{spec.hint}</span>
                  ) : null}
                  <select
                    className="h-10 w-full rounded-[var(--radius)] border border-border bg-surface px-2 text-sm"
                    aria-label={`Which of your columns is ${spec.label}`}
                    value={mapping[spec.key] ?? ''}
                    onChange={(e) => {
                      setMapping((m) => {
                        const next = { ...m };
                        if (e.target.value) next[spec.key] = e.target.value;
                        else delete next[spec.key];
                        return next;
                      });
                      // Touched by a human — it stops being a guess either way.
                      setGuessed((g) => {
                        const next = new Set(g);
                        next.delete(spec.key);
                        return next;
                      });
                      preview.reset();
                    }}
                  >
                    <option value="">— don't import this —</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </label>
              );
            })}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              onClick={() => {
                setError(null);
                commit.reset();
                preview.mutate({ entity, csv, mapping });
              }}
              disabled={preview.isPending || unmappedRequired.length > 0}
            >
              {preview.isPending
                ? 'Checking…'
                : unmappedRequired.length > 0
                  ? `Still need ${unmappedRequired.map((f) => f.label).join(' and ')}`
                  : 'Check my file'}
            </Button>
            <span className="text-xs text-muted-foreground">
              Nothing is saved by checking.
            </span>
          </div>
        </SectionCard>
      ) : null}

      {/* ── 4. Check it ──────────────────────────────────────────────────── */}
      {previewData && !commitData ? (
        <SectionCard title="4 · Check it before anything is saved">
          <div className="mb-3 flex flex-wrap gap-2">
            <StatusBadge tone="neutral">
              {previewData.total} rows in your file
            </StatusBadge>
            <StatusBadge tone={previewData.validCount > 0 ? 'success' : 'neutral'}>
              {previewData.validCount} ready to import
            </StatusBadge>
            {previewData.errorCount > 0 ? (
              <StatusBadge tone="warn">{previewData.errorCount} need a look</StatusBadge>
            ) : null}
          </div>

          {entity === 'price_book' ? (
            <StockImportCounts data={previewData} preview />
          ) : null}
          <ImportWarnings rows={previewData.warnings} />
          {entity === 'price_book' &&
          previewData.validCount === 0 &&
          previewData.errorCount === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nothing needs importing. Initialized quantities and costs will be kept.
            </p>
          ) : null}

          {previewData.sample.length > 0 ? (
            <>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                The first few, as they'll be saved
              </p>
              <DataTable<Record<string, string>>
                columns={fields
                  .filter((f) => mapping[f.key])
                  .map((f) => ({
                    key: f.key,
                    label: f.label,
                    cell: (row) => row[f.key] || '—',
                  }))}
                rows={previewData.sample.map((row, i) => ({
                  ...row,
                  _rowKey: String(i),
                }))}
                rowKey={(row) => row._rowKey!}
                empty={null}
              />
            </>
          ) : null}

          {previewData.errors.length > 0 ? (
            <div className="mt-3">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Rows that won't import
              </p>
              <ul className="max-h-56 space-y-1 overflow-y-auto text-sm">
                {previewData.errors.map((e, i) => (
                  <li
                    key={i}
                    className="rounded-[var(--radius-sm)] bg-surface-2 px-2 py-1"
                  >
                    {e.row > 0 ? (
                      <>
                        <span className="font-semibold">Row {e.row}</span>
                        {/* The row's own values, so it can be found in a 900-row sheet. */}
                        {e.values && Object.values(e.values).some(Boolean) ? (
                          <span className="text-muted-foreground">
                            {' — '}
                            {Object.values(e.values)
                              .filter(Boolean)
                              .slice(0, 3)
                              .join(', ')}
                          </span>
                        ) : null}
                        <span className="block text-xs text-[var(--sw-danger-text)]">
                          {e.message}
                        </span>
                      </>
                    ) : (
                      <span className="text-[var(--sw-danger-text)]">{e.message}</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-muted-foreground">
                These are skipped — the rest still import. Fix them in your spreadsheet
                and bring the file back if you want them.
              </p>
            </div>
          ) : null}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              onClick={() => {
                setError(null);
                commit.mutate({
                  entity,
                  csv,
                  mapping,
                  clientId: idemKey,
                  mutationId: idemKey,
                });
              }}
              disabled={commit.isPending || previewData.validCount === 0}
            >
              {commit.isPending
                ? 'Importing…'
                : `Import ${previewData.validCount} ${ENTITY_LABEL[entity].toLowerCase()}`}
            </Button>
            <Button variant="ghost" onClick={() => preview.reset()}>
              Back to the columns
            </Button>
          </div>
        </SectionCard>
      ) : null}

      {/* ── Done ─────────────────────────────────────────────────────────── */}
      {commitData ? (
        <SectionCard title="Done">
          <EmptyState
            icon={IconCustomers}
            title={
              entity === 'price_book'
                ? 'Price book import complete'
                : `${commitData.created} ${ENTITY_LABEL[entity].toLowerCase()} imported`
            }
            description={
              [
                commitData.skipped > 0
                  ? `${commitData.skipped} were already here and were left alone`
                  : null,
                commitData.errorCount > 0
                  ? `${commitData.errorCount} were skipped because of a problem in the file`
                  : null,
              ]
                .filter(Boolean)
                .join(' · ') || 'Everything in the file came across.'
            }
          />
          {entity === 'price_book' ? <StockImportCounts data={commitData} /> : null}
          <ImportWarnings rows={commitData.warnings} />
          <ImportWarnings rows={commitData.errors} heading="Rows not imported" />
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={resetFile}>Bring another file over</Button>
          </div>
        </SectionCard>
      ) : null}
    </PageRoot>
  );
}

function StockImportCounts({
  data,
  preview = false,
}: {
  preview?: boolean;
  data: {
    catalogueCreated?: number;
    skuAssigned?: number;
    stockSeeded?: number;
    stockSkipped?: number;
  };
}) {
  return (
    <p className="my-3 text-sm" role="status">
      {data.catalogueCreated ?? 0} new catalogue items · {data.skuAssigned ?? 0} SKUs
      initialized · {data.stockSeeded ?? 0} opening balances{' '}
      {preview ? 'to seed' : 'seeded'} · {data.stockSkipped ?? 0} initialized stock rows
      skipped
    </p>
  );
}
function ImportWarnings({
  rows,
  heading = 'Stock notices',
}: {
  rows?: { row: number; message: string }[];
  heading?: string;
}) {
  if (!rows?.length) return null;
  return (
    <div className="my-3">
      <p className="text-sm font-semibold">{heading}</p>
      <ul className="max-h-56 space-y-1 overflow-y-auto text-sm">
        {rows.map((row, i) => (
          <li key={i}>
            {row.row > 0 ? `Row ${row.row}: ` : ''}
            {row.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
